import { __awaiter, __rest } from "tslib";
import React, { memo, useCallback, useRef } from 'react';
import { Modal } from '../Modal';
import { Form } from '../Form';
import { Card } from '../Card';
import { BodyText } from '../Type';
import { Spinner } from '../Spinner';
import { bool, func, node, object, string, oneOfType } from 'prop-types';
import clsx from 'clsx';
import useCounterIncrementingWhen from '../../hooks/useCounterIncrementingWhen';
import useElementHeight from '../../hooks/useElementHeight';
import useUniqueId from '../../hooks/useUniqueId';
import useModalFooter from './useModalFooter';
import useAsyncInitialValues from './useAsyncInitialValues';
import './FormModal.less';
function FormModal(props) {
    const { children, className, closeAfterSubmit = true, confirmationText = null, disabled, formId, formProps, initialValues, loading, mutators = {}, noCard = false, onClose, onInit, onSubmit, renderButtons, showAllErrors } = props, otherProps = __rest(props, ["children", "className", "closeAfterSubmit", "confirmationText", "disabled", "formId", "formProps", "initialValues", "loading", "mutators", "noCard", "onClose", "onInit", "onSubmit", "renderButtons", "showAllErrors"]);
    const actualFormId = useUniqueId(formId);
    const ref = useRef(null);
    // ensure the form completely restarts from scratch whenever the modal is opened
    const formKey = useCounterIncrementingWhen(otherProps.visible, onInit);
    // ensure we don't close the modal if errors occur during submission
    const handleSubmit = useCallback((...args) => __awaiter(this, void 0, void 0, function* () {
        const submissionErrors = yield onSubmit(...args);
        if (!submissionErrors && closeAfterSubmit)
            onClose();
        return submissionErrors;
    }), [onClose, onSubmit, closeAfterSubmit]);
    // dynamically resize the modal content height to match the form size whenever
    // it changes: fixes issues occuring when loading completes
    const height = useElementHeight(ref);
    const actualChildren = noCard ? children : React.createElement(Card, null, children);
    const { footer, handleRenderFormButtons } = useModalFooter(actualFormId, onClose, renderButtons);
    const asyncInitialValues = useAsyncInitialValues(initialValues, otherProps.visible);
    const actualLoading = loading || asyncInitialValues.loading;
    return (React.createElement(Modal, Object.assign({ variant: 'complex', className: clsx(className, 'kal-form-modal'), width: 0, footer: footer, onClose: onClose }, otherProps),
        React.createElement("div", { className: 'kal-form-modal-content', style: { height } },
            React.createElement("div", { ref: ref }, actualLoading ? (React.createElement(Spinner, { variant: 'large', className: 'form-loading-spinner' })) : (React.createElement(Form, Object.assign({ onSubmit: handleSubmit, initialValues: asyncInitialValues.value, key: formKey, id: actualFormId, disabled: disabled, renderButtons: handleRenderFormButtons, mutators: mutators, showAllErrors: showAllErrors }, formProps), confirmationText ? React.createElement(BodyText, { noTopSpacer: true }, confirmationText) : actualChildren))))));
}
FormModal.propTypes = {
    /** Form contents - usually things like form fields (see Form documentation) */
    children: node,
    /** CSS class applied to the modal */
    className: string,
    /** Controls whether the modal should close or not on submit */
    closeAfterSubmit: bool,
    /** Confirmation text to display in place of the form contents */
    confirmationText: node,
    /** If true, the form is disabled */
    disabled: bool,
    /** Custom form ID. If not set, a random ID will be generated */
    formId: string,
    /** Props to pass to the underlying <Form> component (see Form docs for more info) */
    formProps: object,
    /** Initial values for the form OR an async function that returns a values object */
    initialValues: oneOfType([object, func]),
    /** If true, the content of the form is replaced by a large loading spinner */
    loading: bool,
    /** Form mutators to be passed to the underlying Form */
    mutators: object,
    /** By default, the form contents are displayed in a Card. If true, no Card is shown */
    noCard: bool,
    /** Callback activated when the modal is to be closed */
    onClose: func.isRequired,
    /** Callback activated when the modal is opened and the form re-initialised */
    onInit: func,
    /** The submit function for the form. If spreading modal props onto this component, onSubmit will need to appear after spreading the modal props! */
    onSubmit: func.isRequired,
    /** Renders the modal buttons */
    renderButtons: func,
    /** Controls whether the modal is shown or not */
    visible: bool,
    /** This is passed directly to the Form element, causing validation to occur immediately rather than waiting for user interaction */
    showAllErrors: bool,
};
export default memo(FormModal);
//# sourceMappingURL=FormModal.js.map